<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import type { OzDropdownRow, OzDropdownSizePreset } from '@@/library/v4/components/OzDropdown.vue'
import OzDropdown from '@@/library/v4/components/OzDropdown.vue'
import OzDropdownButton, { OzDropdownButtonSizePreset } from '@@/library/v4/components/OzDropdownButton.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import type { VueCssClass } from '@@/types'
import { shiftUpWhenOverflow } from '@@/vuecomposables/floating'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { flip, offset } from '@floating-ui/vue'
import { uniqueId } from 'es-toolkit/compat'
import { computed, ref } from 'vue'

withDefaults(
  defineProps<{
    fieldLabel: string
    buttonText: string
    dropdownAriaLabel: string
    rows: OzDropdownRow[]
    fieldLabelDescription?: string
    // styling
    darkMode?: boolean | 'auto'
    dropdownSizePreset?: OzDropdownSizePreset
    dropdownRowCommonSettings?: OzDropdownRow
    dropdownContainerClass?: VueCssClass
    disabled?: boolean
    // test ids
    buttonTestId?: string
    dropdownTestId?: string
  }>(),
  {
    fieldLabelDescription: undefined,
    darkMode: 'auto',
    dropdownSizePreset: undefined,
    dropdownRowCommonSettings: undefined,
    dropdownContainerClass: undefined,
    disabled: false,
    buttonTestId: undefined,
    dropdownTestId: undefined,
  },
)

const emit = defineEmits<{
  (name: 'select-row', event: Event, payload: any): void
  (name: 'close'): void
}>()

const isDropdownOpen = ref(false)

const onRowSelect = (event: Event, payload: any): void => {
  isDropdownOpen.value = false
  emit('select-row', event, payload)
}

const onClose = (): void => {
  isDropdownOpen.value = false
  emit('close')
}

const button = ref<InstanceType<typeof OzDropdownButton> | null>(null)
const buttonEl = computed(() => button.value?.$el as HTMLElement)

const popoverConfig: ComputePositionConfig = {
  middleware: [offset(4), flip(), shiftUpWhenOverflow()],
  placement: 'bottom-end',
}

const dropdownValueDescriptionId = uniqueId('dropdown-value-description')
</script>
<script lang="ts">
export {
  OzDropdownFocusStyle as OzRowDropdownFocusStyle,
  OzDropdownRowStyle as OzRowDropdownRowStyle,
  OzDropdownSizePreset as OzRowDropdownSizePreset,
} from '@@/library/v4/components/OzDropdown.vue'
export type { OzDropdownRow as OzRowDropdownRow } from '@@/library/v4/components/OzDropdown.vue'
export default {}
</script>

<template>
  <div class="flex flex-row justify-between items-center w-full py-3">
    <div class="flex flex-col flex-1 pe-3">
      <p
        :class="[
          'text-body',
          {
            'text-dark-text-100': darkMode === false,
            'text-light-text-100': darkMode === true,
            'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto',
          },
        ]"
      >
        {{ fieldLabel }}
      </p>
      <p
        v-if="fieldLabelDescription"
        :class="[
          'text-body-small',
          {
            'text-dark-text-200': darkMode === false,
            'text-light-text-200': darkMode === true,
            'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
          },
        ]"
      >
        {{ fieldLabelDescription }}
      </p>
    </div>
    <p :id="dropdownValueDescriptionId" class="sr-only">
      {{ __('The current %{fieldLabel} is %{fieldValue}.', { fieldLabel, fieldValue: buttonText }) }}
    </p>
    <div class="flex items-center justify-end">
      <OzDropdownButton
        ref="button"
        :dir="dir()"
        :disabled="disabled"
        :dark-mode="darkMode"
        :size-preset="OzDropdownButtonSizePreset.H32px"
        :text="buttonText"
        :data-testid="buttonTestId"
        aria-haspopup="menu"
        :aria-expanded="isDropdownOpen"
        :aria-describedby="dropdownValueDescriptionId"
        @click="isDropdownOpen = !isDropdownOpen"
      />
      <OzPopover
        v-if="isDropdownOpen"
        :anchor="buttonEl"
        :config="popoverConfig"
        :dark-mode="darkMode"
        @scrim-click="onClose"
        @scrim-esc="onClose"
      >
        <OzDropdown
          :rows="rows"
          :menu-aria-label="dropdownAriaLabel"
          :dark-mode="darkMode"
          :dir="dir()"
          :class="['shadow-elevation-2', 'z-dropdown', 'w-64', 'max-h-[calc(100vh-24px)]']"
          :size-preset="dropdownSizePreset"
          :data-testid="dropdownTestId"
          :container-class="dropdownContainerClass"
          :row-common-settings="dropdownRowCommonSettings"
          @select-row="onRowSelect"
        />
      </OzPopover>
    </div>
  </div>
</template>
